import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface TableSkeletonProps {
  /** Column header labels (also drives column count) */
  columns: string[];
  /** Number of placeholder rows to render */
  rows?: number;
  /** Optional minimum width for horizontal scroll */
  minWidth?: string;
}

/**
 * Reusable skeleton placeholder for tables while data loads.
 * Mirrors the structure of the real table to prevent layout shift.
 */
export const TableSkeleton = ({ columns, rows = 5, minWidth }: TableSkeletonProps) => {
  return (
    <div className="overflow-x-auto">
      <Table className={minWidth ? `min-w-[${minWidth}]` : undefined}>
        <TableHeader>
          <TableRow className="border-border">
            {columns.map((col) => (
              <TableHead key={col} className="text-muted-foreground">
                {col}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIdx) => (
            <TableRow key={rowIdx} className="border-border">
              {columns.map((_, colIdx) => (
                <TableCell key={colIdx}>
                  <Skeleton
                    className="h-4"
                    style={{
                      // Vary widths so it doesn't look like a perfect grid
                      width: `${50 + ((rowIdx * 7 + colIdx * 13) % 45)}%`,
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/**
 * Compact list-style skeleton for card-based list views.
 */
export const ListSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <div className="space-y-3 p-4">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <Skeleton className="h-6 w-16 rounded-full shrink-0" />
      </div>
    ))}
  </div>
);
